<template>
  <div>
    <fieldset>
      <datepicker-with-timezone
        :initial-time="startDatetimeMoment"
        :initial-timezone="initialStartTimezone"
        :datetime-field-name="startDatetimeFieldName"
        :timezone-field-name="singleTimezoneMode ? null : startTimezoneFieldName"
        :required="required"
        :disabled="isDisabled"
        :time-suggestions="timeSuggestions"
        @change="setStartDatetimeMoment($event)"
        @change:timezone="setStartTimezone($event)"
        :row-label="t('start')"
      >
      </datepicker-with-timezone>
    </fieldset>
    <fieldset v-bind:class="{ field_with_errors: endDatetimeErrors.length > 0 }">
      <datepicker-with-timezone
        :initial-time="endDatetimeMoment"
        :initial-timezone="initialEndTimezone"
        :datetime-field-name="endDatetimeFieldName"
        :timezone-field-name="singleTimezoneMode ? null : endTimezoneFieldName"
        :required="required"
        :disabled="isDisabled"
        :time-suggestions="timeSuggestions"
        @change="setEndDatetimeMoment($event)"
        @change:timezone="initialEndTimezone = $event"
        :row-label="t('end')"
      >
      </datepicker-with-timezone>
      <div v-for="error in endDatetimeErrors" :key="error" class="error">{{error}}</div>
    </fieldset>
    <fieldset v-if="showDurationField" class="datepicker-duration">
      <label class="label">{{ t('duration') }}</label>
      <input
        type="text"
        class="input"
        style="max-width: 160px"
        :value="durationText"
        :placeholder="t('duration_placeholder')"
        :disabled="isDisabled"
        @change="onDurationInput"
      >
      <div v-if="durationError" class="error">{{ durationError }}</div>
    </fieldset>
    <fieldset v-if="singleTimezoneMode" class="activity-timezone">
      <label class="label">{{ t('timezone') }}</label>
      <select
        class="select select--full"
        v-model="activityTimezone"
        :disabled="isDisabled"
        @change="onActivityTimezoneChange"
      >
        <option v-for="option in timezoneOptions" :value="option.value" :key="option.value">{{ option.text }}</option>
      </select>
      <input type="hidden" :name="startTimezoneFieldName" :value="submittedStartTimezone">
      <input type="hidden" :name="endTimezoneFieldName" :value="submittedEndTimezone">
    </fieldset>
  </div>
</template>

<style lang="scss">
// Give the single Activity Time Zone a little breathing room after Duration so
// it reads as a distinct scheduling setting rather than another duration field.
// Design-system fieldsets have margin: 0; this is a subtle, one-off nudge.
.activity-timezone {
  margin-top: 10px;
}
</style>


<script>
import DatepickerWithTimezone from "./datepicker_with_timezone.vue";
import moment from 'moment';
import { parse as parseDuration, format as formatDuration } from '../duration';
import { elapsedSeconds, endWallFromStart } from '../duration_times';
import { displayTimezone, submittedTimezones } from '../activity_timezone';
import { timezones } from '../constants/timezones';
import I18n from 'i18n-js/index.js.erb';
const t = (key, opts) =>
  I18n.t(key, {
    scope: 'js.datepicker_range_with_timezone',
    ...opts,
  });

export default {
  props: [
    'startDatetimeFieldName',
    'endDatetimeFieldName',
    'startTimezoneFieldName',
    'endTimezoneFieldName',
    'startDatetime',
    'endDatetime',
    'startTimezone',
    'endTimezone',
    'endDatetimeError',
    'disabled',
    'required',
    'showDuration',
    'singleTimezone',
    'timeSuggestions'
  ],

  data: function() {
    return {
      initialStartTimezone: this.startTimezone,
      initialEndTimezone: this.endTimezone,
      isDisabled: String(this.disabled) === "true",
      startDatetimeMoment: moment(this.startDatetime),
      endDatetimeMoment: moment(this.endDatetime),
      endDatetimeErrors: JSON.parse(this.endDatetimeError),
      durationText: "",
      durationError: null,
      activityTimezone: displayTimezone(this.startTimezone),
      timezoneTouched: false
    }
  },

  components: {
    DatepickerWithTimezone
  },

  computed: {
    showDurationField() {
      return String(this.showDuration) === "true";
    },
    singleTimezoneMode() {
      return String(this.singleTimezone) === "true";
    },
    timezoneOptions() {
      return timezones;
    },
    submittedStartTimezone() {
      return this.resolvedTimezones().start;
    },
    submittedEndTimezone() {
      return this.resolvedTimezones().end;
    }
  },

  watch: {
    // When either Start or End changes, recompute the derived Duration.
    startDatetimeMoment() {
      this.recomputeDurationFromTimes();
    },
    endDatetimeMoment() {
      this.recomputeDurationFromTimes();
    }
  },

  mounted() {
    this.recomputeDurationFromTimes();
  },

  methods: {
    t,

    identifierFor(timezoneValue) {
      const zone = timezones.find(option => option.value == timezoneValue);
      return zone ? zone.identifier : null;
    },

    wallClockOf(datetimeMoment) {
      if (!datetimeMoment || !datetimeMoment.isValid()) return null;
      return moment(datetimeMoment).format("YYYY-MM-DDTHH:mm:ss");
    },

    // Start or End changed -> refresh the Duration display (elapsed time).
    recomputeDurationFromTimes() {
      if (!this.showDurationField) return;

      const seconds = elapsedSeconds(
        this.wallClockOf(this.startDatetimeMoment),
        this.identifierFor(this.initialStartTimezone),
        this.wallClockOf(this.endDatetimeMoment),
        this.identifierFor(this.initialEndTimezone)
      );
      this.durationError = null;
      this.durationText = seconds !== null && seconds >= 0 ? formatDuration(seconds) : "";
    },

    // Duration changed -> recompute End from Start + Duration (Start anchors).
    // Invalid input shows an error and leaves End unchanged (no auto-correct).
    onDurationInput(event) {
      const seconds = parseDuration(event.target.value);
      if (seconds === null) {
        this.durationError = t("duration_invalid");
        this.durationText = event.target.value;
        return;
      }

      this.durationError = null;
      this.durationText = formatDuration(seconds);

      const endWall = endWallFromStart(
        this.wallClockOf(this.startDatetimeMoment),
        this.identifierFor(this.initialStartTimezone),
        seconds,
        this.identifierFor(this.initialEndTimezone)
      );
      if (endWall) this.setEndDatetimeMoment(moment(endWall));
    },

    resolvedTimezones() {
      return submittedTimezones({
        startTimezone: this.startTimezone,
        endTimezone: this.endTimezone,
        selectedTimezone: this.activityTimezone,
        touched: this.timezoneTouched
      });
    },

    // Explicit organizer change: set both underlying zones to the selection and
    // re-derive Duration (wall-clock Start/End values are left untouched).
    onActivityTimezoneChange() {
      this.timezoneTouched = true;
      this.initialStartTimezone = this.activityTimezone;
      this.initialEndTimezone = this.activityTimezone;
      this.recomputeDurationFromTimes();
    },

    setStartDatetimeMoment(value) {
      value = moment(value);
      var changeDifference = value.diff(this.startDatetimeMoment, "minutes");
      var endString = moment(this.endDatetimeMoment).add(changeDifference, "minutes").toISOString();
      this.setEndDatetimeMoment(endString);
      this.startDatetimeMoment = value;
    },

    setEndDatetimeMoment(value) {
      value = moment(value);
      this.endDatetimeMoment = value;
    },

    setStartTimezone(value) {
      if (this.initialStartTimezone === this.initialEndTimezone) {
        this.initialEndTimezone = value;
      }
      this.initialStartTimezone = value;
    }
  }
}
</script>
